import { t } from "i18next";
import * as z from "zod";

/**
 * Schémas des formulaires des coffres d'épargne (savings-service, guide
 * v3.1 §6.10).
 *
 * Factory (règle projet) : créés via `create*Schema()` et mémoïsés au
 * niveau composant sur la langue courante — jamais d'instance exportée
 * figée. Les montants sont saisis en unité MAJEURE et convertis en unité
 * mineure (`majorToMinor`) au moment de construire le payload (§4.3).
 */

const UUID_REGEXP =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const uuidField = () =>
  z
    .string()
    .trim()
    .regex(UUID_REGEXP, { message: t("invalidFieldError") });

/** Création d'un coffre (`POST /SavingsVaults/add`). */
export const createSavingsVaultSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .max(80, { message: t("invalidFieldError") }),
    /** Objectif facultatif en unité majeure (0 / vide = sans objectif). */
    goalAmount: z
      .number()
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    mainWalletId: uuidField(),
  });

export type SavingsVaultFormValues = z.infer<
  ReturnType<typeof createSavingsVaultSchema>
>;

/** Renommage / objectif d'un coffre (`PUT /SavingsVaults/{id}`). */
export const createUpdateSavingsVaultSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .max(80, { message: t("invalidFieldError") }),
    /** 0 / vide = retirer l'objectif (goalAmountMinor null). */
    goalAmount: z
      .number()
      .min(0, { message: t("invalidFieldError") })
      .optional(),
  });

export type UpdateSavingsVaultFormValues = z.infer<
  ReturnType<typeof createUpdateSavingsVaultSchema>
>;

/** Versement / retrait (`deposit` / `withdraw`). */
export const createSavingsMovementSchema = () =>
  z.object({
    mainWalletId: uuidField(),
    /** Montant en unité majeure, strictement positif. */
    amount: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .positive({ message: t("invalidFieldError") }),
  });

export type SavingsMovementFormValues = z.infer<
  ReturnType<typeof createSavingsMovementSchema>
>;

/**
 * Blocage / prolongation (`POST /SavingsVaults/{id}/lock`) — échéance UTC
 * dans la fenêtre serveur 1–1 825 jours, jamais avant l'échéance en cours.
 */
export const createLockSavingsVaultSchema = (currentLockedUntil?: string | null) =>
  z.object({
    lockedUntil: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .refine(
        (value) => {
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return false;
          const min = Date.now() + 24 * 60 * 60 * 1000;
          const max = Date.now() + 1825 * 24 * 60 * 60 * 1000;
          return date.getTime() >= min && date.getTime() <= max;
        },
        { message: t("invalidFieldError") }
      )
      .refine(
        (value) =>
          !currentLockedUntil ||
          new Date(value).getTime() >= new Date(currentLockedUntil).getTime(),
        { message: t("invalidFieldError") }
      ),
  });

export type LockSavingsVaultFormValues = z.infer<
  ReturnType<typeof createLockSavingsVaultSchema>
>;

/** Demande maker-checker `SAVINGS_EARLY_UNLOCK` (motif métier obligatoire). */
export const createEarlyUnlockRequestSchema = () =>
  z.object({
    reason: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
  });

export type EarlyUnlockRequestFormValues = z.infer<
  ReturnType<typeof createEarlyUnlockRequestSchema>
>;

/** Exécution du déblocage anticipé (demande APPROVED existante). */
export const createEarlyUnlockExecuteSchema = () =>
  z.object({
    approvalRequestId: uuidField(),
  });

export type EarlyUnlockExecuteFormValues = z.infer<
  ReturnType<typeof createEarlyUnlockExecuteSchema>
>;
